import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useCompetition } from '../competition/CompetitionContext';
import './AuditTrail.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const AuditTrail = () => {
  const { user } = useAuth();
  const { selectedCompetition } = useCompetition();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Common action types for filtering
  const actionTypes = [
    'LOGIN',
    'LOGOUT',
    'CREATE_EVENT',
    'UPDATE_EVENT',
    'DELETE_EVENT',
    'CREATE_COMPETITION',
    'UPDATE_COMPETITION',
    'SELECT_COMPETITION',
    'CREATE_DANCER',
    'UPDATE_DANCER',
    'DELETE_DANCER',
    'ADD_DANCER_TO_EVENT',
    'REMOVE_DANCER_FROM_EVENT',
    'CREATE_COURSE',
    'UPDATE_COURSE',
    'DELETE_COURSE',
    'UPLOAD_IMAGE',
    'DELETE_IMAGE'
  ];

  const fetchAuditLogs = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page,
        limit: 50
      });

      if (actionFilter) params.append('action', actionFilter);
      if (userFilter) params.append('userId', userFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      // Filter by current competition
      if (selectedCompetition && selectedCompetition.id) {
        params.append('competitionId', selectedCompetition.id);
      }

      const response = await fetch(`https://${_host}:6003/api/${_company}/audit-logs?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const data = await response.json();
      setLogs(data.logs || []);
      setTotalPages(data.pagination?.pages || 0);
      setTotalCount(data.pagination?.total || 0);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching audit logs:', err);

      // Check if it's a server error (endpoint not available yet)
      if (err.message.includes('Failed to fetch')) {
        setError('Audit trail feature is not yet available. The server needs to be restarted to enable audit logging.');
      } else {
        setError('Failed to load audit logs. Please try again later.');
      }

      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs(currentPage);
  }, [currentPage, actionFilter, userFilter, startDate, endDate, selectedCompetition]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAuditLogs(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    setActionFilter('');
    setUserFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-ZA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionColor = (action) => {
    const colors = {
      'LOGIN': '#28a745',
      'LOGOUT': '#dc3545',
      'CREATE_EVENT': '#007bff',
      'UPDATE_EVENT': '#ffc107',
      'DELETE_EVENT': '#dc3545',
      'CREATE_COMPETITION': '#28a745',
      'UPDATE_COMPETITION': '#ffc107',
      'SELECT_COMPETITION': '#17a2b8',
      'CREATE_DANCER': '#28a745',
      'UPDATE_DANCER': '#ffc107',
      'DELETE_DANCER': '#dc3545',
      'UPLOAD_IMAGE': '#6f42c1',
      'DELETE_IMAGE': '#dc3545'
    };
    return colors[action] || '#6c757d';
  };

  const filteredLogs = logs.filter(log => {
    const searchLower = searchTerm.toLowerCase();
    return (
      log.action?.toLowerCase().includes(searchLower) ||
      log.userId?.toLowerCase().includes(searchLower) ||
      log.details?.toLowerCase().includes(searchLower) ||
      log.targetId?.toLowerCase().includes(searchLower) ||
      log.targetType?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="audit-trail-container">
      <div className="audit-trail-header">
        <h1>Audit Trail</h1>
        <p>
          {selectedCompetition
            ? `Showing audit logs for: ${selectedCompetition.name}`
            : 'Monitor all user activities and system events'
          }
        </p>
      </div>

      {/* Search and Filter Section */}
      <div className="search-filters">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-row">
            <div className="search-field">
              <label>Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search actions, users, details..."
              />
            </div>

            <div className="search-field">
              <label>Action Type</label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
              >
                <option value="">All Actions</option>
                {actionTypes.map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>

            <div className="search-field">
              <label>User ID</label>
              <input
                type="text"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                placeholder="Filter by user..."
              />
            </div>

            <div className="search-field">
              <label>Start Date</label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="search-field">
              <label>End Date</label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="search-actions">
            <button type="submit" className="search-btn">Search</button>
            <button type="button" onClick={handleReset} className="reset-btn">Reset</button>
          </div>
        </form>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <span>Total Records: {totalCount}</span>
        <span>Page {currentPage} of {totalPages}</span>
      </div>

      {/* Audit Logs Table */}
      <div className="audit-logs-table-container">
        {loading ? (
          <div className="loading">Loading audit logs...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filteredLogs.length === 0 ? (
          <div className="no-records">No audit logs found matching your criteria</div>
        ) : (
          <table className="audit-logs-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Details</th>
                <th>Target Type</th>
                <th>Target ID</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id} className="log-row">
                  <td className="timestamp">
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td className="user-id">
                    {log.userId}
                  </td>
                  <td className="action">
                    <span
                      className="action-badge"
                      style={{ backgroundColor: getActionColor(log.action) }}
                    >
                      {log.action}
                    </span>
                  </td>
                  <td className="details">
                    {log.details || '-'}
                  </td>
                  <td className="target-type">
                    {log.targetType || '-'}
                  </td>
                  <td className="target-id">
                    {log.targetId || '-'}
                  </td>
                  <td className="ip-address">
                    {log.ip || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditTrail;